import { Button, ButtonVariant } from "@att-bit/duc.components.button";
import React, { useState } from "react";
import { AnalyticsEvent } from "@att-bit/ui-frameworks.components.analytics-event";
import { docComplete } from "@att-bit/duc.utils.types";

export type AccordionExpandAlignments = "center" | "right";

export type AccordionProps = {
  /**  children (text/jsx) to be rendered in component  */
  children: React.ReactNode;
  /** Initial children to view limited accordion items */
  initialChildren?: React.ReactNode;
  /** Used to create a uniqueId for the Accordion (make sure it is unique!) */
  name: string;
  /** event that is fired when the expand all button is toggled */
  onExpandAll?: (isOpen: boolean) => void;
  /** determines where the expand all button is aligned to */
  expandAlignment?: AccordionExpandAlignments;
  /** passes classes to the expand all button */
  expandBtnClassName?: string;
  /** pass either a JSX element or a string to the Accordion's title */
  title?: any;
  /** passes classes to the Accordion's title */
  titleClassName?: string;
  /** toggles whether the expand all button shows */
  withExpand?: boolean;
  /** Sets "data-testid" attribute of the container */
  testId?: string;
  /** for adding class names necessary for adding padding around accordion items */
  classNames?: string;
  /** event trigger when accordion item expanded or collapsed. */
  onItemToggle?: (val: boolean, idx: number) => void;
  /** Defining theme */
  theme?: string;
  /** Defining initial accordion items count */
  initialAccCount?: string;
  /** Expand All Text */
  expandAllText?: string;
  /** Collapse All Text */
  collapseAllText?: string;
  /**used to supress link analytics by default it is true*/
  suppressAnalytics?: boolean;
  /** reads compImpress and sends in accordion item link clicks. */
  compImpression?: any;
  /** sets the containerId for in page navigation. */
  containerId?: string;
  /** accessibility text for expand/collapse button*/
  expandAccessibilityText?: string;
  collapseAccessibilityText?: string;
  viewMoreAccessibilityText?: string;
  viewLessAccessibilityText?: string;
};

Accordion.deafaultProps = {
  // theme: {
  //   backgroundColor: tokens['color-gray-200'],
  //   color: tokens['color-black'],
  // }
};

export function Accordion({
  children,
  initialChildren,
  name,
  expandAlignment = "center",
  expandBtnClassName,
  title,
  titleClassName,
  withExpand,
  testId,
  onExpandAll,
  onItemToggle,
  classNames,
  theme,
  initialAccCount,
  expandAllText = "Expand all",
  collapseAllText = "Collapse all",
  compImpression,
  suppressAnalytics = true,
  containerId,
  expandAccessibilityText = "Expand all",
  collapseAccessibilityText = "Collapse all",
  viewMoreAccessibilityText = "View more",
  viewLessAccessibilityText = "View less",
  ...props
}: AccordionProps) {
  const initIsOpenArr = [];
  let jsonLdObj = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": []
  };
  let mainEntityArr = [];
  React.Children.map(children, (child, idx) => {
    // istanbul ignore next
    if (React.isValidElement(child)) {
      let title = formatTitleAndBody(child?.props?.children[0]?.props?.children,'title');
      let body = formatTitleAndBody(child?.props?.children[1]?.props?.children,'body');
      mainEntityArr.push({
        "@type": "Question",
        "name": title,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": body
        }  
      });
      initIsOpenArr.push(child.props.isOpen);
    }
  });
  jsonLdObj['mainEntity'] = mainEntityArr;

  //validate JSON
  const isValidJSON = str => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };
 
  //Extract title & body to form the josnld schema code
  function formatTitleAndBody(data, type) {
    let getType = typeof data;
    if ( getType === 'object' && type === 'title') {
      return data?.props?.children;
    } else if ( getType === 'object' && type === 'body' ) {
      return data[0]?.props?.children?.props?.children;
    }
    return data;
  }
  //end

  const [state, setState] = useState({
    isExpandAll: initIsOpenArr.indexOf(false) !== -1,
    isOpenArr: initIsOpenArr
  });
  const { isExpandAll, isOpenArr } = state;
  const [viewToggleText, setViewToggleText] = useState('View more');

  // HOOKS ##############################################################

  // METHODS #############################################################
  const sendAnalyticsEvent = (linkObj: any) => {
    const lnkClickObj = {
      eventCode: "Link_Click",
      eventAction: "linkClick",
      additionalData: linkObj,
    };
    docComplete(() => {
      AnalyticsEvent(lnkClickObj);
    });
  };

  const toggleExpandAll = (e: any) => {
    const { key } = e;
    // istanbul ignore next
    if (key ? key === "Enter" || key === " " : true) {
      const { isOpenArr, isExpandAll } = state;
      if (isExpandAll) {
        for (let i = 0; i < isOpenArr.length; i++) {
          isOpenArr[i] = true;
        }
      } else {
        for (let i = 0; i < isOpenArr.length; i++) {
          isOpenArr[i] = false;
        }
      }
      setState({
        isExpandAll: !isOpenArr[0],
        isOpenArr: isOpenArr.slice()
      });
      if (onExpandAll) {
        onExpandAll(!isOpenArr[0]);
      }
      if (!suppressAnalytics && compImpression) {
        const lnkObj = {
          linkName: e.target?.innerText,
          linkPosition: "accordion",
          ...compImpression
        }
        sendAnalyticsEvent(lnkObj);
      }
    }
  };

  const toggleItems = () => {
    if(viewToggleText === 'View more') {
      setViewToggleText('View less');
    }
    else {
      setViewToggleText('View more');
    }
    if (!suppressAnalytics && compImpression) {
      const lnkObj = {
        linkName: viewToggleText === 'View more' ? 'View more' : 'View less',
        linkPosition: "accordion",
        ...compImpression
      }
      sendAnalyticsEvent(lnkObj);
    }
  };

  const updateExpandAll = (value: boolean, index: number) => {
    const { isOpenArr, isExpandAll } = state;
    isOpenArr[index] = value;
    if (isOpenArr.indexOf(false) === -1 && isExpandAll === true) {
      setState({
        isExpandAll: false,
        isOpenArr: isOpenArr.slice()
      });
    } else if (isOpenArr.indexOf(true) === -1 && isExpandAll === false) {
      setState({
        isExpandAll: true,
        isOpenArr: isOpenArr.slice()
      });
    }
    if (!suppressAnalytics && compImpression) {
      const tmpCompImp = {...compImpression, contentFriendlyName: "accordion_Question" + (index+1)};
      const lnkObj = {
        linkName: mainEntityArr[index]?.name,
        linkPosition: "accordion",
        ...tmpCompImp
      }
      sendAnalyticsEvent(lnkObj);
    }
    if (onItemToggle) {
      onItemToggle(value, index);
    }
  };

  // RENDER ##############################################################
  return (
    <>
    <div 
      {...containerId && containerId!=='' && {
        id: containerId
      }}
    className={`max-width-background ${theme ? ('bgcolor ' + theme) : ''}`}>
      {
        isValidJSON(JSON.stringify(jsonLdObj)) ? 
        <script
          type="application/ld+json"
          key="item-jsonld"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdObj) }}
        /> : ''
      }
      <div 
        className={`${classNames ? classNames : 'pad-t-xl pad-b-xl '} container align-items-center flex flex-column text-center`}
        data-testid={testId}
      >
        <div className="row flex-wrap">
          <div className="grid-col-12">
            {typeof title === "string" ? (
              <h2
                className={titleClassName ? titleClassName : "heading-xxl mar-b-md"}
              >
                {title}
              </h2>
            )
            :
            title?.toJsx({margin: ["mar-b-md"]})}

        {withExpand && (
          <div
            className={`flex ${
              expandAlignment === "right" ? "justify-end" : "justify-center"
            }${expandBtnClassName ? ` ${expandBtnClassName}` : " mar-b-xl"}`}
          >
            <a
              className="link-text3 solo"
              onKeyUp={toggleExpandAll}
              onMouseDown={toggleExpandAll}
              tabIndex={0}
              role="button"
              aria-label= {isExpandAll ? expandAccessibilityText : collapseAccessibilityText}
            >
              {isExpandAll ? expandAllText : collapseAllText}
            </a>
          </div>
        )}

        <div className="border-top border-gray-300">
          {/*children*/}
         {React.Children.map(initialAccCount && (viewToggleText === 'View more') ? initialChildren : children, (child, idx) => {
            // istanbul ignore next
            if (React.isValidElement(child)) {
              return React.cloneElement(child as React.ReactElement<any>, {
                testId: `${name ? name : ""}-${idx}`,
                isOpen: isOpenArr[idx],
                theme: theme,
                onToggle: (open: boolean) => {
                  updateExpandAll(open, idx);
                },
                key: idx,
              });
            }
          })}
        </div>
        {initialAccCount !== undefined && <div className="text-center pad-t-md-all"><Button accessibilityText={(viewToggleText === 'View more') ? viewMoreAccessibilityText : viewLessAccessibilityText} variant = {ButtonVariant.WHITE} onClick={() => { toggleItems() }}>{viewToggleText}</Button></div>}
        </div>
        </div>
      </div>
    </div>
    </>
  );
}
